/**
 * Pestañas de datos del panel inferior (RR-28): «Información» (estado, última posición, registro,
 * placa, fuentes y cliente) y «Sensores externos».
 *
 * Es lo que WOX abre abajo al tocar un vehículo. Solo lectura sobre el equipo: el botón es para
 * editar el registro; ninguno le manda nada al GPS (constitución, Art. 6).
 */

import { useId } from "react";
import { compareWithSources, type ComparisonState, type RastreoRow } from "@core/rastreo/registro.ts";
import {
  DOCUMENT_TYPE_LABEL,
  PROTOCOL_LABEL,
  SIM_OPERATOR_LABEL,
} from "@core/contracts/rastreo-registro.ts";
import {
  ago,
  bogotaTime,
  CLOCK_LABEL,
  CONNECTION_LABEL,
  describeAttributes,
  fixLabel,
  FRESHNESS_LABEL,
  IGNITION_LABEL,
  measure,
  MODEL_STATUS_LABEL,
  ROW_STATE_CLASS,
  ROW_STATE_LABEL,
} from "./rastreo-format.ts";
import { EditIcon, PlusIcon } from "./rastreo-icons.tsx";

const GRID = "grid gap-x-8 md:grid-cols-2 xl:grid-cols-3";

export function InfoTab({
  row,
  now,
  onEdit,
  onRegister,
}: {
  row: RastreoRow;
  now: number;
  onEdit: () => void;
  onRegister: () => void;
}) {
  const vehicle = row.vehicle;
  const position = vehicle?.lastPosition ?? null;
  const equipo = row.equipo;
  const cliente = row.cliente;

  return (
    <div className="px-4 py-3">
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        <span className={`font-semibold ${ROW_STATE_CLASS[row.state]}`}>{ROW_STATE_LABEL[row.state]}</span>
        {vehicle ? (
          <>
            <span className="text-gd-muted">Última conexión {ago(vehicle.lastConnectionAt, now)}</span>
            <span className={vehicle.telemetryFreshness === "fresh" ? "text-emerald-700" : "text-amber-700"}>
              {FRESHNESS_LABEL[vehicle.telemetryFreshness]} · {ago(vehicle.lastTelemetryAt, now)}
            </span>
          </>
        ) : null}
        {equipo === null ? <span className="font-semibold text-gd-blue">Sin registrar</span> : null}
        {equipo ? (
          <button type="button" onClick={onEdit} className="gd-btn-secondary ml-auto min-h-9 px-3 text-[13px]">
            <EditIcon className="size-4" /> Editar datos
          </button>
        ) : (
          <button type="button" onClick={onRegister} className="gd-btn-primary ml-auto min-h-9 px-3 text-[13px]">
            <PlusIcon className="size-4" /> Registrar este equipo
          </button>
        )}
      </p>

      {position && vehicle && vehicle.telemetryFreshness !== "fresh" ? (
        <p role="note" className="mt-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-950">
          <strong className="font-semibold">Esta no es la posición actual.</strong> Es la última que mandó el equipo,{" "}
          {ago(position.deviceTime ?? position.serverReceivedAt, now)}.
        </p>
      ) : null}

      {equipo && !equipo.motorDeviceId ? (
        <p role="note" className="mt-2 rounded-xl border border-gd-line bg-gd-mist px-3 py-2 text-[13px] text-gd-ink">
          Este equipo no quedó registrado en el motor de rastreo: cuando se guardó, el motor no estaba conectado. Ábrelo
          en «Editar datos» y guarda otra vez para registrarlo.
        </p>
      ) : null}

      <div className={GRID}>
        {position ? <LastPositionSections position={position} /> : null}
        {equipo ? (
          <Section title="Registro del equipo">
            <Item label="Placa" value={equipo.placa ?? "sin placa"} />
            <Item label="Modelo" value={equipo.modelo} />
            <Item label="IMEI" value={`•••• ${equipo.identificadorFinal}`} />
            <Item label="Protocolo" value={PROTOCOL_LABEL[equipo.protocolo]} />
            <Item label="SIM" value={`${equipo.simNumero} · ${SIM_OPERATOR_LABEL[equipo.simOperador]}`} wide />
            <Item label="ICCID" value={equipo.simIccid ?? "sin dato"} />
            <Item label="Ciudad" value={equipo.ciudad} />
            <Item label="Instalado" value={equipo.instaladoEn ?? "sin dato"} />
            <Item label="Vínculo desde" value={equipo.vigenteDesde ? bogotaTime(equipo.vigenteDesde) : "sin vínculo"} />
            {equipo.notas ? <Item label="Notas" value={equipo.notas} wide /> : null}
          </Section>
        ) : vehicle ? (
          <Section title="Equipo del motor">
            <Item label="Modelo" value={vehicle.model ?? "sin registrar"} />
            <Item label="Estado del modelo" value={MODEL_STATUS_LABEL[vehicle.modelStatus]} />
            <Item label="Protocolo" value={vehicle.sourceProtocol ?? "sin dato"} />
            <Item label="Conectividad" value={CONNECTION_LABEL[vehicle.connectionStatus]} />
          </Section>
        ) : null}

        {equipo?.placa ? <SourcesByPlate row={row} placa={equipo.placa} /> : null}

        {cliente ? (
          <Section title="Cliente (ficha del área de GPS)">
            <Item label="Nombre" value={cliente.nombre} wide />
            <Item label={DOCUMENT_TYPE_LABEL[cliente.tipoDocumento]} value={cliente.documento} />
            <Item label="Teléfono" value={cliente.telefono} />
            <Item label="Dirección" value={cliente.direccion} wide />
            <Item label="Ciudad" value={cliente.ciudad} />
            <Item label="Póliza" value={cliente.poliza ?? "sin póliza"} />
          </Section>
        ) : equipo ? (
          <p className="mt-3 text-[14px] text-gd-muted">Este equipo no tiene cliente asignado.</p>
        ) : null}
      </div>
    </div>
  );
}

/** La última posición con todo lo que mandó el equipo: es parte de «Información». */
function LastPositionSections({ position }: { position: NonNullable<RastreoRow["vehicle"]>["lastPosition"] & object }) {
  const attributes = describeAttributes(position.attributes);
  return (
    <>
      <Section title="Última posición">
        <Item label="Hora del equipo" value={bogotaTime(position.deviceTime)} />
        <Item label="Recibida" value={bogotaTime(position.serverReceivedAt)} />
        <Item label="Velocidad" value={measure(position.speedKph, "km/h", 1)} />
        <Item label="Rumbo" value={measure(position.headingDeg, "°")} />
        <Item label="Ignición" value={IGNITION_LABEL[position.ignition]} />
        <Item label="Señal GPS" value={fixLabel(position.fixValid)} />
        <Item label="Satélites" value={measure(position.satellites, "")} />
        <Item label="Altitud" value={measure(position.altitudeM, "m")} />
        <Item label="Coordenadas" value={`${position.latitude.toFixed(6)}, ${position.longitude.toFixed(6)}`} wide />
        <Item label="Reloj" value={CLOCK_LABEL[position.clockQuality]} wide />
      </Section>
      {attributes.length > 0 ? (
        <Section title="Estado que mandó el GPS">
          {attributes.map(([label, value]) => (
            <Item key={label} label={label} value={value} />
          ))}
        </Section>
      ) : null}
    </>
  );
}

/**
 * Sensores externos: los que un cliente conecte al GPS (temperatura, nivel de combustible, etc.).
 * El motor todavía no lee ninguno, así que esta pestaña lo dice en vez de mostrar datos inventados.
 * La batería, el voltaje y la señal del propio GPS están en «Información».
 */
export function ExternalSensorsTab({ row }: { row: RastreoRow }) {
  const attributes = row.vehicle?.lastPosition?.attributes ?? {};
  const found = (["fuel", "temperature"] as const).flatMap((key) => {
    const value = attributes[key];
    return value === undefined || value === null ? [] : [[key === "fuel" ? "Combustible" : "Temperatura", String(value)] as const];
  });
  return (
    <div className="px-4 py-3">
      {found.length > 0 ? (
        <Section title="Sensores externos">
          {found.map(([label, value]) => (
            <Item key={label} label={label} value={value} />
          ))}
        </Section>
      ) : (
        <p className="text-[14px] text-gd-muted">
          <strong className="font-semibold text-gd-navy">Este equipo no reporta sensores externos.</strong> Aquí se
          verán los que el cliente conecte al GPS, como temperatura o nivel de combustible, cuando el motor los lea.
          La batería, el voltaje y la señal del propio GPS están en «Información».
        </p>
      )}
    </div>
  );
}

const SYSTEM_LABEL: Record<string, string> = {
  sync: "Sync/Softseguros",
  highlevel: "HighLevel",
  alegra: "Alegra",
  wox: "WOX",
  manual: "Manual",
};

const STATE_TEXT: Record<ComparisonState, { label: string; tone: string }> = {
  coincide: { label: "Coincide", tone: "text-emerald-700" },
  difiere: { label: "Difiere", tone: "text-amber-800 font-semibold" },
  sin_fuente: { label: "Sin dato en las fuentes", tone: "text-gd-muted" },
  sin_manual: { label: "Falta en la ficha", tone: "text-gd-blue" },
};

/**
 * Lo que Sync/Softseguros y HighLevel dicen de la placa, al lado de la ficha manual.
 *
 * La ficha es el registro del área de GPS y se llena a mano; esto no la reemplaza ni la
 * corrige sola. Si algo difiere, se dice y lo resuelve una persona.
 */
function SourcesByPlate({ row, placa }: { row: RastreoRow; placa: string }) {
  const relacion = row.relacion;
  if (!relacion) {
    return (
      <Section title={`Sync/Softseguros y HighLevel · ${placa}`}>
        <p className="col-span-2 py-1.5 text-[13px] text-gd-muted">
          Drive todavía no tiene datos de Sync/Softseguros ni de HighLevel para esta placa. Llegan con la carga de
          atributos por placa; la ficha manual sigue siendo el registro del área de GPS.
        </p>
      </Section>
    );
  }
  const source = (campo: string) => {
    const origen = relacion.fuentes[campo];
    return origen ? `${SYSTEM_LABEL[origen.system] ?? origen.system} · ${bogotaTime(origen.observedAt)}` : null;
  };
  const vehiculo = [relacion.marca, relacion.linea, relacion.modeloAnio].filter(Boolean).join(" ");
  const comparacion = compareWithSources(row.cliente, relacion);
  return (
    <Section title={`Sync/Softseguros y HighLevel · ${placa}`}>
      <Item label="Aseguradora" value={relacion.aseguradora ?? "sin dato"} />
      <Item label="Póliza" value={relacion.numeroPoliza ?? "sin dato"} note={source("numero_poliza")} />
      <Item label="Titular" value={relacion.titularNombre ?? "sin dato"} note={source("titular_nombre")} wide />
      <Item label="Documento del titular" value={relacion.titularDocumento ?? "sin dato"} note={source("titular_documento")} />
      <Item label="Ciudad" value={relacion.ciudad ?? "sin dato"} />
      <Item label="Vehículo" value={vehiculo || "sin dato"} wide />
      <div className="col-span-2 mt-2">
        <p className="text-[12px] text-gd-muted">Frente a la ficha manual</p>
        <ul className="mt-1 space-y-1">
          {comparacion.map((item) => (
            <li key={item.campo} className="flex flex-wrap justify-between gap-x-3 text-[13px]">
              <span className="text-gd-navy">{item.campo}</span>
              <span className={STATE_TEXT[item.estado].tone}>
                {STATE_TEXT[item.estado].label}
                {item.estado === "difiere" ? `: la ficha dice «${item.manual}»` : ""}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const titleId = useId();
  return (
    <section className="mt-3" aria-labelledby={titleId}>
      <h3 id={titleId} className="font-display text-[13px] font-bold tracking-wide text-gd-blue uppercase">
        {title}
      </h3>
      <dl className="mt-1.5 grid grid-cols-2 gap-x-4">{children}</dl>
    </section>
  );
}

function Item({ label, value, wide = false, note = null }: { label: string; value: string; wide?: boolean; note?: string | null }) {
  return (
    <div className={`min-w-0 border-b border-gd-line py-1.5 ${wide ? "col-span-2" : ""}`}>
      <dt className="text-[12px] text-gd-muted">{label}</dt>
      <dd className="m-0 text-[14px] font-medium break-words text-gd-navy">
        {value}
        {note ? <span className="block text-[11px] font-normal text-gd-muted">{note}</span> : null}
      </dd>
    </div>
  );
}
